// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/HomeSessionHost.tsx
// 🔧 PURPOSE: Tutto quello che la Home apre "sopra" le sue viste, in un
//             posto solo (prima stava dentro HeroSection):
//             - il login (GuestLoginModal). openLogin(returnTo) lo apre e,
//               SOLO se l'accesso riesce, porta l'ospite dove voleva andare
//               ("Codice porta" del riquadro "Con l'accesso" →
//               /my-stay?open=door). La pagina d'arrivo non potrebbe farlo da
//               sola: il suo ?open= si perde se l'ospite non è ancora dentro.
//               Lo apre anche chi torna con lo stato del router
//               { openLogin: true } (ClaimBooking, VacationPlanner);
//             - la chat con la reception (GuestChatDrawer), anche dal vecchio
//               evento `openGuestChat` (FloatingChatButton, le notifiche);
//               sessione scaduta dalla chat → si esce, ma la chat resta
//               aperta con la card "Accedi di nuovo": il login si apre con
//               codice ed email della sessione scaduta e, riuscito, si torna
//               alla chat (annullando si torna alla chat, con la card);
//             - l'Assistente AI (JotFormChatModal);
//             - la scelta "Come vuoi scriverci?": toccando "Reception 24h"
//               l'ospite dentro sceglie fra l'Assistente AI (risposta
//               immediata) e la reception (una persona dello staff, con il
//               pallino dei non letti). Chi non è dentro va dritto all'AI.
//
//             Le viste usano useHomeSession(): openLogin, openChat,
//             openAssistant, openReception e il conteggio dei non letti (uno
//             solo per tutta la Home).
// ============================================

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, MessageCircle, Sparkles } from 'lucide-react';
import GuestLoginModal from '../GuestLoginModal';
import GuestChatDrawer from '../GuestChatDrawer';
import JotFormChatModal from '../JotFormChatModal';
import HomeSheet from './HomeSheet';
import { IconCircle, INK_TEXT, MUTED_TEXT, UnreadBadge } from './ui';
import { storedGuestToken, useGuestSession } from '../../hooks/useGuestSession';
import { useNotifications } from '../../hooks/useNotifications';
import { useUnreadMessages } from '../../hooks/useUnreadMessages';
import { unreadBadgeStore } from '../../lib/unreadBadge';
import { cn } from '../../lib/utils';

/** L'evento storico che apre la chat della reception (FloatingChatButton, notifiche). */
export const OPEN_GUEST_CHAT_EVENT = 'openGuestChat';

export interface HomeSessionApi {
  /**
   * Apre il login. Con `returnTo` (un percorso dell'app), dopo un accesso
   * riuscito porta lì; se l'ospite è già dentro ci va subito.
   */
  openLogin: (returnTo?: string) => void;
  /** La chat con la reception (una persona). Senza accesso: prima il login, poi la chat. */
  openChat: () => void;
  /** L'Assistente AI (JotForm), per tutti. */
  openAssistant: () => void;
  /** "Reception 24h": dentro, la scelta AI / reception; fuori, l'Assistente AI. */
  openReception: () => void;
  /** I messaggi dello staff non ancora letti (0 fuori dalla sessione). */
  unreadCount: number;
}

const HomeSessionContext = createContext<HomeSessionApi | null>(null);

/** Fuori dall'host non si apre niente (lo si dice in console, non all'ospite). */
const NO_HOST: HomeSessionApi = {
  openLogin: () => console.warn('HomeSessionHost assente: il login non si apre'),
  openChat: () => console.warn('HomeSessionHost assente: la chat non si apre'),
  openAssistant: () => console.warn("HomeSessionHost assente: l'assistente non si apre"),
  openReception: () => console.warn('HomeSessionHost assente: la reception non si apre'),
  unreadCount: 0,
};

export const useHomeSession = (): HomeSessionApi => useContext(HomeSessionContext) || NO_HOST;

/** Cosa fare dopo un accesso riuscito dal login aperto qui. */
type AfterLogin = { type: 'navigate'; to: string } | { type: 'chat' };

/** Codice ed email della sessione scaduta, per il login di "Accedi di nuovo". */
interface LoginPrefill {
  bookingReference: string;
  email: string;
}

const HomeSessionHost: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isLoggedIn,
    isLoading,
    token,
    booking,
    guestName,
    error,
    errorCode,
    login,
    logout,
    clearError,
  } = useGuestSession();
  const { deviceId } = useNotifications();
  const { unreadCount, lastChecked } = useUnreadMessages(token, isLoggedIn);

  // Lo stesso numero per la barra in basso, anche fuori dalla Home
  // (lib/unreadBadge): nessuna richiesta in piu'. Si scrive dopo il primo
  // controllo, cosi' rientrando nella Home il numero di prima non sparisce
  // per un attimo (lo 0 iniziale dello hook non e' un conteggio).
  useEffect(() => {
    if (token && lastChecked) unreadBadgeStore.publish(token, unreadCount);
  }, [token, lastChecked, unreadCount]);

  const [loginOpen, setLoginOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  // La chat con la sessione scaduta resta aperta anche dopo l'uscita: la
  // card "Accedi di nuovo" è lì. Il ref serve all'effetto dell'uscita, che
  // può girare prima che lo stato arrivi.
  const [chatExpired, setChatExpired] = useState(false);
  const chatExpiredRef = useRef(false);
  const expiredPrefillRef = useRef<LoginPrefill | null>(null);
  // Il login aperto da "Accedi di nuovo": già compilato, e dice che si torna alla chat.
  const [loginPrefill, setLoginPrefill] = useState<LoginPrefill | null>(null);

  // La destinazione dopo il login vale solo per QUEL login: si svuota alla
  // chiusura, riuscito o no.
  const afterLoginRef = useRef<AfterLogin | null>(null);
  const loggedInHereRef = useRef(false);

  const showLogin = useCallback(() => {
    // Un errore di un tentativo vecchio (o di un'uscita forzata) non si rivede.
    clearError();
    setChooserOpen(false);
    setLoginOpen(true);
  }, [clearError]);

  const openLogin = useCallback((returnTo?: string) => {
    // Già dentro (per esempio entrato da un'altra scheda): si va e basta.
    if (returnTo && storedGuestToken()) {
      navigate(returnTo);
      return;
    }
    afterLoginRef.current = returnTo ? { type: 'navigate', to: returnTo } : null;
    showLogin();
  }, [navigate, showLogin]);

  const openChat = useCallback(() => {
    setChooserOpen(false);
    // Letto dallo store al momento: subito dopo un login lo hook di questo
    // render potrebbe non saperlo ancora.
    if (!storedGuestToken()) {
      afterLoginRef.current = { type: 'chat' };
      showLogin();
      return;
    }
    // Passa dall'evento storico: lo ascoltano anche i non letti, che si
    // azzerano subito (il drawer segna la lettura sul server appena carica).
    window.dispatchEvent(new CustomEvent(OPEN_GUEST_CHAT_EVENT));
  }, [showLogin]);

  const openAssistant = useCallback(() => {
    setChooserOpen(false);
    setAssistantOpen(true);
  }, []);

  const openReception = useCallback(() => {
    if (!storedGuestToken()) {
      setAssistantOpen(true);
      return;
    }
    setChooserOpen(true);
  }, []);

  // La chat si apre dall'evento, da chiunque arrivi, solo con l'ospite dentro.
  useEffect(() => {
    const onOpen = () => { if (storedGuestToken()) setChatOpen(true); };
    window.addEventListener(OPEN_GUEST_CHAT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_GUEST_CHAT_EVENT, onOpen);
  }, []);

  // Uscito (dal menu, per scadenza, da un'altra scheda): chat e scelta si
  // chiudono. Non la chat con la sessione scaduta: aspetta il nuovo accesso.
  // Di nuovo dentro, quella chat torna una chat normale.
  useEffect(() => {
    if (isLoggedIn) {
      chatExpiredRef.current = false;
      expiredPrefillRef.current = null;
      setChatExpired(false);
      return;
    }
    if (!chatExpiredRef.current) setChatOpen(false);
    setChooserOpen(false);
  }, [isLoggedIn]);

  // Si torna da "Collega la tua prenotazione" (o dal planner) con il login
  // da riaprire. Lo stato si consuma subito, così un refresh non lo riapre.
  useEffect(() => {
    const state = location.state as { openLogin?: boolean } | null;
    if (state?.openLogin) {
      afterLoginRef.current = null;
      showLogin();
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleLogin = async (bookingRef: string, email: string): Promise<boolean> => {
    const ok = await login(bookingRef, email, deviceId);
    if (ok) loggedInHereRef.current = true;
    return ok;
  };

  // Il modal chiama onClose sia annullando sia dopo un accesso riuscito:
  // la destinazione si usa solo nel secondo caso.
  const closeLogin = () => {
    setLoginOpen(false);
    setLoginPrefill(null);
    const next = afterLoginRef.current;
    const success = loggedInHereRef.current;
    afterLoginRef.current = null;
    loggedInHereRef.current = false;
    if (!success || !next) return;
    if (next.type === 'navigate') navigate(next.to);
    else window.dispatchEvent(new CustomEvent(OPEN_GUEST_CHAT_EVENT));
  };

  // La chat ha trovato la sessione scaduta: si esce (la Home sotto torna
  // "senza accesso"), ma la chat resta aperta con la card. Codice ed email
  // si tengono per il login di "Accedi di nuovo".
  const handleSessionExpired = () => {
    expiredPrefillRef.current = {
      bookingReference: booking?.bookingReference || '',
      email: booking?.guestEmail || '',
    };
    chatExpiredRef.current = true;
    setChatExpired(true);
    logout();
  };

  // "Accedi di nuovo": il login già compilato; riuscito, di nuovo la chat.
  const handleChatRelogin = () => {
    afterLoginRef.current = { type: 'chat' };
    setLoginPrefill(expiredPrefillRef.current || { bookingReference: '', email: '' });
    showLogin();
  };

  const closeChat = () => {
    setChatOpen(false);
    if (chatExpiredRef.current) {
      chatExpiredRef.current = false;
      expiredPrefillRef.current = null;
      setChatExpired(false);
    }
  };

  const api = useMemo<HomeSessionApi>(
    () => ({ openLogin, openChat, openAssistant, openReception, unreadCount }),
    [openLogin, openChat, openAssistant, openReception, unreadCount],
  );

  return (
    <HomeSessionContext.Provider value={api}>
      {children}

      <GuestLoginModal
        isOpen={loginOpen}
        onClose={closeLogin}
        onLogin={handleLogin}
        isLoading={isLoading}
        error={error}
        errorCode={errorCode}
        redirectToChat={loginPrefill !== null}
        initialBookingReference={loginPrefill ? loginPrefill.bookingReference : null}
        initialEmail={loginPrefill ? loginPrefill.email : null}
      />

      <GuestChatDrawer
        isOpen={chatOpen && (isLoggedIn || chatExpired)}
        onClose={closeChat}
        token={token}
        guestName={guestName}
        onSessionExpired={handleSessionExpired}
        onRelogin={handleChatRelogin}
        isNonRefundable={booking?.isNonRefundable}
      />

      <JotFormChatModal isOpen={assistantOpen} onClose={() => setAssistantOpen(false)} />

      <ReceptionChooser
        open={chooserOpen && isLoggedIn}
        onClose={() => setChooserOpen(false)}
        onAssistant={openAssistant}
        onChat={openChat}
        unreadCount={unreadCount}
      />
    </HomeSessionContext.Provider>
  );
};

// ---- La scelta "Come vuoi scriverci?" ----------------------------------------

interface ReceptionChooserProps {
  open: boolean;
  onClose: () => void;
  onAssistant: () => void;
  onChat: () => void;
  unreadCount: number;
}

// Le due strade come nel mockup: l'AI nel cerchio espresso, la reception nel
// blu della testata della chat che si aprirà; una freccia a destra.
const OPTION =
  'flex min-h-[64px] w-full items-center gap-3 rounded-2xl border border-nva-edge bg-nva-card px-3 py-2.5 text-left transition-colors ' +
  'hover:bg-nva-cream/60 dark:border-stone-700 dark:bg-stone-800 dark:hover:bg-stone-700/70 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-nva-sea dark:focus-visible:ring-sky-400 motion-safe:active:scale-[0.99]';

const CHEVRON = 'h-[17px] w-[17px] flex-shrink-0 text-[#7A6F5E] dark:text-stone-500';

const ReceptionChooser: React.FC<ReceptionChooserProps> = ({ open, onClose, onAssistant, onChat, unreadCount }) => {
  const { t } = useTranslation();
  const unreadLabel = t('home.reception.unread', { count: unreadCount });
  return (
    <HomeSheet
      open={open}
      onClose={onClose}
      titleId="home-reception-chooser-title"
      eyebrow={t('home.quick.reception')}
      title={t('home.reception.chooserTitle')}
    >
      <div className="flex flex-col gap-2">
        <button type="button" onClick={onAssistant} className={OPTION}>
          <IconCircle tone="ink" size={38}>
            <Sparkles />
          </IconCircle>
          <span className="flex min-w-0 flex-1 flex-col gap-px">
            <span className={cn('text-[15px] font-bold leading-snug', INK_TEXT)}>{t('home.reception.aiTitle')}</span>
            <span className={cn('text-[13px] leading-snug', MUTED_TEXT)}>{t('home.reception.aiDesc')}</span>
          </span>
          <ChevronRight className={CHEVRON} strokeWidth={2.2} aria-hidden="true" />
        </button>
        <button type="button" onClick={onChat} className={OPTION}>
          <IconCircle size={38} className="bg-blue-600 text-white dark:bg-blue-600 dark:text-white">
            <MessageCircle />
          </IconCircle>
          <span className="flex min-w-0 flex-1 flex-col gap-px">
            <span className={cn('text-[15px] font-bold leading-snug', INK_TEXT)}>{t('home.reception.humanTitle')}</span>
            <span className={cn('text-[13px] leading-snug', MUTED_TEXT)}>{t('home.reception.humanDesc')}</span>
          </span>
          <UnreadBadge count={unreadCount} label={unreadLabel} className="h-[22px] min-w-[22px] border-0 text-[11.5px]" />
          <ChevronRight className={CHEVRON} strokeWidth={2.2} aria-hidden="true" />
        </button>
      </div>
    </HomeSheet>
  );
};

export default HomeSessionHost;
